import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

const ServicesWhySection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isRtl = dir === "rtl";

  const content = (t as any)?.servicesWhy || {};
  const title = content.title || "Why EduVate?";
  const description =
    content.description ||
    "We believe every student deserves access to quality education and every family deserves to feel confident about the decision they're making.\n\nWe don't rush families into choices. We take the time to understand each student's situation, and we stay involved long after the enrollment is done. Every student we work with is treated with the same care and seriousness we'd want for our own children.";
  const image = content.image || "/images/support-intro-graduate.jpg";

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.22 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden bg-stone-100 py-16 sm:py-20 lg:py-28"
      aria-labelledby="services-why-title"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="site-container flex flex-col gap-8 overflow-hidden">
        <div className="flex flex-col gap-6 overflow-hidden">
          <h2
            id="services-why-title"
            className={`font-sans text-2xl font-semibold uppercase leading-tight text-teal-900 transition-all duration-700 ease-out sm:text-[32px] sm:leading-[1.2] ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            } ${isRtl ? "text-right" : "text-left"}`}
          >
            {title}
          </h2>

          <div
            className="grid items-stretch gap-5 lg:grid-cols-[minmax(0,1.28fr)_minmax(0,1fr)] lg:gap-6"
          >
            <div
              className={`relative flex min-h-[360px] flex-col justify-start overflow-hidden rounded-lg bg-second-color px-5 py-6 transition-all duration-[900ms] ease-out sm:min-h-[420px] sm:px-8 sm:py-8 lg:min-h-[500px] ${
                isVisible ? "translate-x-0 opacity-100" : isRtl ? "translate-x-10 opacity-0" : "-translate-x-10 opacity-0"
              } ${isRtl ? "lg:order-2" : ""}`}
            >
              <div
                className={`relative z-10 max-w-[34rem] whitespace-pre-line font-sans text-sm font-normal leading-[1.35] text-main-color sm:text-base ${
                  isRtl ? "ml-auto text-right" : "text-left"
                }`}
              >
                {description}
              </div>
              <img
                src="/images/frame-25-services.svg"
                alt=""
                aria-hidden="true"
                className={`pointer-events-none absolute bottom-0 h-[192px] w-[152px] object-contain transition-all duration-[1200ms] ease-out sm:h-[192px] sm:w-[152px] ${
                  isRtl
                    ? `left-0 scale-x-[-1] ${isVisible ? "translate-x-0 translate-y-0 opacity-100" : "-translate-x-8 translate-y-8 opacity-0"}`
                    : `right-0 ${isVisible ? "translate-x-0 translate-y-0 opacity-100" : "translate-x-8 translate-y-8 opacity-0"}`
                }`}
              />
            </div>

            <div
              className={`relative h-[320px] overflow-hidden rounded-lg transition-all duration-[950ms] ease-out sm:h-[420px] lg:h-[500px] ${
                isVisible ? "translate-x-0 scale-100 opacity-100" : isRtl ? "-translate-x-10 scale-[0.97] opacity-0" : "translate-x-10 scale-[0.97] opacity-0"
              } ${isRtl ? "lg:order-1" : ""}`}
            >
              <img
                src={image}
                alt=""
                className={`h-full w-full object-cover object-[center_35%] transition-transform duration-[1600ms] ease-out ${
                  isVisible ? "scale-100" : "scale-110"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesWhySection;
